import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  FC,
  ForwardedRef,
  ReactNode,
  RefObject,
} from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { getBaseActionClasses } from 'src/components/buttons-and-actions/button';
import { TableHeaderLabel } from 'src/components/data-display/table-header-label';
import { ArrowDownIcon } from 'src/components/media-and-icons/icons/arrow-down';
import { ArrowUpIcon } from 'src/components/media-and-icons/icons/arrow-up';
import { ArrowUpDownIcon } from 'src/components/media-and-icons/icons/arrow-up-down';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useHeaderHeight } from 'src/hooks/use-header-height';
import { cn } from 'src/utils/cn';

// this is a workaround to stay compatible with the old styles.
// we should improve it once we remove the old styles.
// see https://prisma.atlassian.net/browse/UXX-866
function getColsWidths(widths: (string | number)[]) {
  const relativeWidths = widths.filter((col) => typeof col === 'number');
  const relativeWidthsSum = relativeWidths.reduce((a, b) => a + b, 0);

  const vars: Record<string, string> = {};
  widths.forEach((width, index) => {
    const value =
      typeof width === 'string'
        ? `calc(${width} + 2 * 0.8rem)`
        : `calc(100% * ${width / relativeWidthsSum})`;
    vars[`--col-w-${index + 1}`] = value;
  });

  // in styled-components these have been generated dynamically, but tailwind
  // needs static classes, so we "prebuild" them.
  // sadly the drawback is that the amount cannot be fully dynamic.
  // an upper limit of 30 columns per table should be ok for the moment.
  const classes = cn(
    widths.length >= 1 && `[&_td:nth-child(1),&_th:nth-child(1)]:w-(--col-w-1)`,
    widths.length >= 2 && `[&_td:nth-child(2),&_th:nth-child(2)]:w-(--col-w-2)`,
    widths.length >= 3 && `[&_td:nth-child(3),&_th:nth-child(3)]:w-(--col-w-3)`,
    widths.length >= 4 && `[&_td:nth-child(4),&_th:nth-child(4)]:w-(--col-w-4)`,
    widths.length >= 5 && `[&_td:nth-child(5),&_th:nth-child(5)]:w-(--col-w-5)`,
    widths.length >= 6 && `[&_td:nth-child(6),&_th:nth-child(6)]:w-(--col-w-6)`,
    widths.length >= 7 && `[&_td:nth-child(7),&_th:nth-child(7)]:w-(--col-w-7)`,
    widths.length >= 8 && `[&_td:nth-child(8),&_th:nth-child(8)]:w-(--col-w-8)`,
    widths.length >= 9 && `[&_td:nth-child(9),&_th:nth-child(9)]:w-(--col-w-9)`,
    widths.length >= 10 &&
      `[&_td:nth-child(10),&_th:nth-child(10)]:w-(--col-w-10)`,
    widths.length >= 11 &&
      `[&_td:nth-child(11),&_th:nth-child(11)]:w-(--col-w-11)`,
    widths.length >= 12 &&
      `[&_td:nth-child(12),&_th:nth-child(12)]:w-(--col-w-12)`,
    widths.length >= 13 &&
      `[&_td:nth-child(13),&_th:nth-child(13)]:w-(--col-w-13)`,
    widths.length >= 14 &&
      `[&_td:nth-child(14),&_th:nth-child(14)]:w-(--col-w-14)`,
    widths.length >= 15 &&
      `[&_td:nth-child(15),&_th:nth-child(15)]:w-(--col-w-15)`,
    widths.length >= 16 &&
      `[&_td:nth-child(16),&_th:nth-child(16)]:w-(--col-w-16)`,
    widths.length >= 17 &&
      `[&_td:nth-child(17),&_th:nth-child(17)]:w-(--col-w-17)`,
    widths.length >= 18 &&
      `[&_td:nth-child(18),&_th:nth-child(18)]:w-(--col-w-18)`,
    widths.length >= 19 &&
      `[&_td:nth-child(19),&_th:nth-child(19)]:w-(--col-w-19)`,
    widths.length >= 20 &&
      `[&_td:nth-child(20),&_th:nth-child(20)]:w-(--col-w-20)`,
    widths.length >= 21 &&
      `[&_td:nth-child(21),&_th:nth-child(21)]:w-(--col-w-21)`,
    widths.length >= 22 &&
      `[&_td:nth-child(22),&_th:nth-child(22)]:w-(--col-w-22)`,
    widths.length >= 23 &&
      `[&_td:nth-child(23),&_th:nth-child(23)]:w-(--col-w-23)`,
    widths.length >= 24 &&
      `[&_td:nth-child(24),&_th:nth-child(24)]:w-(--col-w-24)`,
    widths.length >= 25 &&
      `[&_td:nth-child(25),&_th:nth-child(25)]:w-(--col-w-25)`,
    widths.length >= 26 &&
      `[&_td:nth-child(26),&_th:nth-child(26)]:w-(--col-w-26)`,
    widths.length >= 27 &&
      `[&_td:nth-child(27),&_th:nth-child(27)]:w-(--col-w-27)`,
    widths.length >= 28 &&
      `[&_td:nth-child(28),&_th:nth-child(28)]:w-(--col-w-28)`,
    widths.length >= 29 &&
      `[&_td:nth-child(29),&_th:nth-child(29)]:w-(--col-w-29)`,
    widths.length >= 30 &&
      `[&_td:nth-child(30),&_th:nth-child(30)]:w-(--col-w-30)`
  );

  // in case a table has more than 30 columns (which can happen in edge cases for GDM where tables
  // have no upper limit), we don't want the page to crash, but we do not support correct sizing of columns
  // anymore. this is fine as the columns become so narrow anyway that good reading cannot be expected anymore.
  // for such a case using the table component in the first place might not be a good idea.
  if (widths.length > 30)
    console.warn(
      'Tables with more than 30 columns do not fully support correct sizing of columns.'
    );

  return { classes, vars };
}

export type SimpleTableProps = {
  respectHeader?: boolean;
  /**
   * Either specify a fixed width like `'5.5rem'` as a string or a relative width
   * like `1` as a number which would be treated just like `'1fr'` in a CSS grid.
   *
   * Let's say you have a table like this:
   * `tsx
   * <SimpleTable widths={[2, 1, '5.5rem']} />
   * `
   *
   * This means your last column has a width of 5.5rem (plus padding which will be added later)
   * and the other two columns get the remaining space will the first one will be 66.6% and the
   * second one 33.3%.
   */
  widths: (string | number)[];
  sticky?: boolean;
  children: ReactNode;
  light?: boolean;
  containsFormFields?: boolean;
  className?: string;
  empty?: boolean;
};

/**
 * Don't use `<SimpleTable/>` directly. Prefer `<Table/>` which offers a better
 * API and wraps this component.
 * @deprecated
 */
export const SimpleTable: FC<SimpleTableProps> = ({
  respectHeader = true,
  children,
  light,
  containsFormFields = false,
  widths,
  sticky,
  className,
  empty,
}) => {
  const header = useHeaderHeight();
  const colsWidths = getColsWidths(widths);
  return (
    <table
      style={
        {
          // without the -0.5px, the sticky header would be slightly cut off on the top
          // and the background could leak through while scrolling.
          '--th-top': respectHeader
            ? `calc(${header.height}px - 0.5px)`
            : '-0.5px',
          ...colsWidths.vars,
        } as CSSProperties
      }
      className={cn(
        // The table clip rounds the header at rest; sticky cells become square as it scrolls away.
        `
          group/table relative w-full table-fixed border-separate
          border-spacing-0 overflow-clip rounded-xs
          [clip-path:inset(0_round_var(--radius-xs))]
        `,
        sticky && '[&_th]:sticky [&_th]:top-(--th-top)',
        empty && 'rounded-b-none',
        colsWidths.classes,
        className
      )}
      {...{ 'data-contains-form-fields': containsFormFields }}
      {...(light ? { 'data-light': true } : {})} // children use this as a styling hook with the `group-data-light/table:` variant
    >
      {children}
    </table>
  );
};

export const Thead: FC<ComponentPropsWithRef<'thead'>> = (props) => {
  return (
    <thead
      {...props}
      className={cn(
        // sticky needed for nested tables to properly overlay each other
        'sticky z-1',
        props.onClick && 'cursor-pointer',
        props.className
      )}
    />
  );
};

export type TrProps = {
  isSelected?: boolean;
  isExpanded?: boolean;
} & ComponentPropsWithRef<'tr'>;

export const Tr: FC<TrProps> = ({ isSelected, isExpanded, ...props }) => {
  return (
    <tr
      {...props}
      className={cn(
        `
          group/tr bg-background-surface-default
          hover:not-group-data-light/table:bg-background-surface-minimal
        `,
        // default:
        //   td - bottom border
        //   th - top border
        `
          [&_td]:border-b [&_td]:border-b-border-surface-minimal
          [&_th]:border-t [&_th]:border-t-border-surface-minimal
        `,
        // first row:
        //   td - top border
        `
          first:not-group-data-light/table:[&_td]:border-t
          first:not-group-data-light/table:[&_td]:border-t-border-surface-minimal
        `,
        // first column:
        //   td - left border and bottom border
        //   th - left border and top border
        `
          [&_td]:first:border-l [&_td]:first:border-border-surface-minimal
          [&_th]:first:border-l [&_th]:first:border-border-surface-minimal
        `,
        // last column:
        //   td - right border and bottom border
        //   th - right border and top border
        `
          [&_td]:last:border-r [&_td]:last:border-border-surface-minimal
          [&_th]:last:border-r [&_th]:last:border-border-surface-minimal
        `,
        // last row: bottom rounded corners (the top ones are owned by the clipping at the table)
        `
          last:[&_td]:last:rounded-br-xs
          last:[&_td]:first:rounded-bl-xs
        `,
        isExpanded &&
          // remove border bottom of the row (the border bottom is owned by the expanded row)
          // left border as hard shadow to depict "active" state of the row
          // continuity of the table borders, applied to the expanded row
          // applied border bottom to the expanded row, so the row and its expanded area are braced with a border, acting like a single row
          `
            [&_td]:border-b-0!
            [&_td]:first:shadow-[inset_3px_0_0_var(--color-border-surface-active)]
            [&+tr>td]:border-r [&+tr>td]:border-b
            [&+tr>td]:border-border-surface-minimal
          `,
        isSelected &&
          // apply selected background
          // apply selected border at the bottom of this row and of the previous row
          // apply selected border at the top of the first row
          `
            bg-background-support-highlight-minimal!
            [&_td]:border-b-border-support-highlight-subdued!
            first:[&_td]:border-t-border-support-highlight-subdued!
            [tr:has(+&)>td]:border-b-border-support-highlight-subdued!
          `,
        isExpanded &&
          isSelected &&
          `[&+tr>td]:border-b-border-support-highlight-subdued`,
        props.onClick && 'cursor-pointer',
        props.className
      )}
    />
  );
};

type Align = 'left' | 'center' | 'right';

const baseCellStyle = cn(
  `
    p-md
    group-data-light/table:py-sm
  `
);

export const thStyle = cn(
  baseCellStyle,
  `
    bg-background-surface-subdued font-label-semibold
    group-data-light/table:bg-background-surface-minimal
  `
);

type ThProps = {
  align?: Align;
  /**
   * Rendered as a small caption above the header label. Usage is highly discouraged,
   * unless there is a very specific need for it.
   */
  colGroup?: ReactNode;
  /**
   * Interactive content in front of the label (e.g. a "select all" checkbox).
   * It is not part of the marquee and never scrolls. Do not use this for regular labels.
   */
  leftAddon?: ReactNode;
} & ComponentPropsWithRef<'th'>;

export const Th: FC<ThProps> = ({
  align = 'left',
  colGroup,
  leftAddon,
  children,
  ...props
}) => {
  const label = (
    <TableHeaderLabel colGroup={colGroup}>{children}</TableHeaderLabel>
  );

  return (
    <th
      {...props}
      className={cn(
        thStyle,
        'group/th',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        props.className
      )}
    >
      {leftAddon ? (
        <span className="inline-flex max-w-full items-baseline gap-xs">
          <span className="shrink-0">{leftAddon}</span>
          {children && <span className="min-w-0 overflow-hidden">{label}</span>}
        </span>
      ) : (
        label
      )}
    </th>
  );
};

const getTdStyle = ({
  mode,
}: {
  mode?: 'highlight' | 'expand-row' | 'default';
}) =>
  cn(
    baseCellStyle,
    'in-data-[contains-form-fields=true]:align-top',
    mode === 'highlight' &&
      `
        bg-background-support-highlight-minimal
        group-hover/tr:bg-background-support-highlight-pastel/30
      `,
    // we let the expand button to own the cell spacing in favor of a better hit area
    mode === 'expand-row' && 'h-0 p-0'
  );

type TdProps = (
  | {
      align?: Align;
      mode?: 'default' | 'highlight';
    }
  | {
      /**
       * For 'mode="expand-row"', the `align` is always set to `'center'`.
       */
      align?: undefined;
      mode: 'expand-row';
    }
) &
  ComponentPropsWithRef<'td'>;

export const Td: FC<TdProps> = ({
  mode,
  align: passedAlign = 'left',

  ...props
}) => {
  const align = mode === 'expand-row' ? 'center' : passedAlign;
  return (
    <td
      {...props}
      className={cn(
        getTdStyle({ mode }),
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        props.className
      )}
    />
  );
};

export type SortableParams = { sortColumn: string; sortAscending: boolean };

type SortableParamsTuple = [
  SortableParams,
  (params: Partial<SortableParams>) => void,
];

type SortDirection = 'asc' | 'desc';

type SortableHeaderProps = {
  label: ReactNode;
  active: boolean;
  direction: SortDirection;
  onSort: (direction: SortDirection) => void;
  button?: (props: {
    buttonProps: ComponentPropsWithoutRef<'button'>;
    labelRef: RefObject<HTMLElement | null>;
  }) => ReactNode;
  info?: ReactNode;
  ref?: ForwardedRef<HTMLDivElement>;
} & ComponentPropsWithoutRef<'button'>;

export const ThButton: FC<ComponentPropsWithoutRef<'button'>> = (props) => {
  return (
    <button
      {...props}
      type="button"
      className={cn(
        getBaseActionClasses({}),
        `
          group/th-button w-full border-0 bg-transparent p-md text-left
          [font-weight:inherit] text-inherit
        `,
        props.className
      )}
    />
  );
};

const labelStyle = cn(getBaseActionClasses({}), 'min-w-0 cursor-[inherit]');

export const SortableHeader: FC<SortableHeaderProps> = (props) => {
  const { label, active, direction, onSort, button, info, ref, ...rest } =
    props;

  const handleSort = useCallback(() => {
    if (direction === 'asc') {
      onSort('desc');
    } else {
      onSort('asc');
    }
  }, [direction, onSort]);

  const sortIcon = useMemo(() => {
    if (!active) return <ArrowUpDownIcon size="xs" />;
    return direction === 'asc' ? (
      <ArrowUpIcon size="xs" />
    ) : (
      <ArrowDownIcon size="xs" />
    );
  }, [direction, active]);

  const ariaLabel = useMemo(() => {
    if (!active) {
      return 'Click to sort this column';
    }

    if (direction === 'desc') {
      return 'Click to enable ascending sorting';
    }

    return 'Click to enable descending sorting';
  }, [direction, active]);

  const labelRef = useRef<HTMLElement>(null);

  const buttonProps: ComponentPropsWithoutRef<'button'> = {
    onClick: (e) => {
      rest.onClick?.(e);
      handleSort();
    },
    className: cn('select-none', rest.className),
    ...rest,
    'aria-label': ariaLabel,
    children: (
      <div
        className={`
          inline-grid max-w-full grid-flow-col grid-cols-[minmax(0,1fr)]
          items-center gap-xs
        `}
        ref={ref}
      >
        <span className={labelStyle}>
          <TableHeaderLabel ref={labelRef}>{label}</TableHeaderLabel>
        </span>
        {!!info && (
          <Tooltip content={info}>
            {(targetProps) => (
              <FontAwesomeIcon icon={faInfoCircle} {...targetProps} />
            )}
          </Tooltip>
        )}
        <span
          aria-hidden
          className={cn(
            'text-label-sm',
            !active && 'text-foreground-surface-subdued'
          )}
        >
          {sortIcon}
        </span>
      </div>
    ),
  };

  return (
    <th className={cn(thStyle, 'group/th p-0', props.className)}>
      {button ? (
        button({ buttonProps, labelRef })
      ) : (
        <ThButton {...buttonProps} />
      )}
    </th>
  );
};

type BasicParams<SortBy extends string> = {
  sortBy: SortBy;
  sortDirection: SortDirection;
};

type BasicPageParams<
  SortBy extends string,
  Params extends BasicParams<SortBy>,
> = {
  set: (value: Partial<Params>) => void;
  value: Params;
};

type DoubleSortableHeaderProps<
  SortBy extends string,
  Params extends BasicParams<SortBy>,
> = {
  labels: [string, string];
  columns: [Params['sortBy'], Params['sortBy']];
  params: BasicPageParams<SortBy, Params>;
  initialSortAscending?: boolean;
  button?: (props: {
    buttonProps: ComponentPropsWithoutRef<'button'>;
    labelRef: RefObject<HTMLElement | null>;
  }) => ReactNode;
} & ComponentPropsWithoutRef<'button'>;

export function DoubleSortableHeader<
  SortBy extends string,
  Params extends BasicParams<SortBy>,
>(props: DoubleSortableHeaderProps<SortBy, Params>) {
  const {
    labels,
    columns,
    params: { set: setParams },
    initialSortAscending = false,
    button,
    ...rest
  } = props;

  let sortColumn = props.params.value.sortBy;
  let sortDirection = props.params.value.sortDirection;
  let active = columns.includes(sortColumn);

  let label = labels[columns.indexOf(sortColumn)]
    ? labels[columns.indexOf(sortColumn)]
    : labels[0];

  const handleSort = useCallback(() => {
    const onSort = (sortDirection: SortDirection, sortBy: SortBy) => {
      if (!active) {
        // If the sorted columns switch, do not toggle the `sortAscending` state, use `initialSortAscending`
        setParams({
          sortBy,
          sortDirection: initialSortAscending ? 'asc' : 'desc',
        } as Partial<Params>);
      } else {
        setParams({ sortBy, sortDirection } as Partial<Params>);
      }
    };

    let currentSortBy = sortColumn;
    let sortAscending = sortDirection === 'asc';

    if (sortAscending && currentSortBy === columns[0]) {
      onSort('desc', columns[0]);
    } else if (!sortAscending && currentSortBy === columns[0]) {
      onSort('asc', columns[1]);
    } else if (sortAscending && currentSortBy === columns[1]) {
      onSort('desc', columns[1]);
    } else if (!sortAscending && currentSortBy === columns[1]) {
      onSort('asc', columns[0]);
    } else {
      onSort('asc', columns[0]);
    }
  }, [
    sortColumn,
    sortDirection,
    setParams,
    active,
    columns,
    initialSortAscending,
  ]);

  const sortIcon = useMemo(() => {
    if (!active) return <ArrowUpDownIcon size="xs" />;
    return sortDirection === 'asc' ? (
      <ArrowUpIcon size="xs" />
    ) : (
      <ArrowDownIcon size="xs" />
    );
  }, [sortDirection, active]);

  const ariaLabel = useMemo(() => {
    if (!active) {
      return 'Click to sort this column';
    }

    if (sortDirection === 'desc') {
      return 'Click to enable ascending sorting';
    }

    return 'Click to enable descending sorting';
  }, [sortDirection, active]);

  const labelRef = useRef<HTMLElement>(null);

  const buttonProps: ComponentPropsWithoutRef<'button'> = {
    onClick: (e) => {
      rest.onClick?.(e);
      handleSort();
    },
    ...rest,
    'aria-label': ariaLabel,
    children: (
      <div
        className={`
          inline-grid max-w-full grid-flow-col grid-cols-[minmax(0,1fr)]
          items-center gap-xs
        `}
      >
        <span className={labelStyle}>
          <TableHeaderLabel ref={labelRef}>{label}</TableHeaderLabel>
        </span>

        <span
          aria-hidden
          className={cn(
            'text-label-sm',
            !active && 'text-foreground-surface-subdued'
          )}
        >
          {sortIcon}
        </span>
      </div>
    ),
  };

  return (
    <th className={cn(thStyle, 'group/th p-0', props.className)}>
      {button ? (
        button({ buttonProps, labelRef })
      ) : (
        <ThButton {...buttonProps} />
      )}
    </th>
  );
}

type GenericSortableHeaderProps = {
  label: ReactNode;
  sortableParams: SortableParamsTuple;
  column: string;
  initialSortAscending?: boolean;
  ref?: ForwardedRef<HTMLDivElement>;
};

export const GenericSortableHeader: FC<GenericSortableHeaderProps> = (
  props
) => {
  const {
    sortableParams: [{ sortColumn, sortAscending }, setParams],
    column,
    initialSortAscending = false,
    ref,
    ...rest
  } = props;

  const active = sortColumn === column;
  const direction = (active ? sortAscending : initialSortAscending)
    ? 'asc'
    : 'desc';

  return (
    <SortableHeader
      direction={direction}
      active={active}
      ref={ref}
      data-testid={`sort-${column}`}
      onSort={() => {
        if (!active) {
          // If the sorted columns switch, do not toggle the `sortAscending` state, use `initialSortAscending`
          setParams({
            sortColumn: column,
            sortAscending: initialSortAscending,
          });
        } else if (sortAscending) {
          setParams({ sortColumn: column, sortAscending: false });
        } else {
          setParams({ sortColumn: column, sortAscending: true });
        }
      }}
      {...rest}
    />
  );
};

type SpecificSortHeaderProps<
  Params extends { [key: string]: unknown },
  SortParam extends keyof Params,
  DirectionParam extends keyof Params,
> = {
  label: ReactNode;
  params: {
    value: Params & Record<DirectionParam, SortDirection>;
    set: (value: Partial<Params>) => void;
  };
  sortParam: SortParam;
  directionParam: DirectionParam;
  column: Params[SortParam];
  initialSortAscending?: boolean;
  button?: (props: {
    buttonProps: ComponentPropsWithoutRef<'button'>;
    labelRef: RefObject<HTMLElement | null>;
  }) => ReactNode;
  info?: ReactNode;
};

export function SpecificSortHeader<
  Params extends { [key: string]: unknown },
  SortParam extends keyof Params,
  DirectionParam extends keyof Params,
>(props: SpecificSortHeaderProps<Params, SortParam, DirectionParam>) {
  const {
    params,
    sortParam,
    directionParam,
    column,
    initialSortAscending = false,
    ...rest
  } = props;

  return (
    <SortableHeader
      direction={params.value[directionParam]}
      active={params.value[sortParam] === column}
      data-testid={`sort-${String(column)}`}
      onSort={(direction) => {
        if (params.value[sortParam] !== column) {
          // If the sorted columns switch, do not toggle the sorting order
          params.set({
            [sortParam]: column,
            [directionParam]: initialSortAscending ? 'asc' : 'desc',
          } as Partial<Params>);
        } else {
          params.set({
            [sortParam]: column,
            [directionParam]: direction,
          } as Partial<Params>);
        }
      }}
      {...rest}
    />
  );
}
